import {
  defineStrictWhiteColor,
  getThemeClassName,
  getThemesByColor,
} from '@components/App/Modal/PublishModal/utils'
import { publishResponseAdapter } from '@components/ArtBoard/Publish/helper'
import { usePublishData } from '@components/ArtBoard/Publish/hook/usePublishData'
import { migrationProjectSelector } from '@store/migrationSelectors/projectSelectors'
import { artBoardActions } from '@store/reducers/artboard'
import type { AppState } from '@store/types'
import {
  type Preset,
  type PublishContent,
  type PublishData,
  type PublishFonts,
  setPreset,
  setPublishBgColor,
  setPublishContent,
  setPublishData,
  setPublishFont,
  setPublishPagePreview,
  setPublishRemovePresetName,
} from '@storeOld/reducers/publish'
import { closeModal } from '@storeOld/reducers/ui'
import { useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import type { Action } from 'redux'
import type { ThunkDispatch } from 'redux-thunk'

import { GtmTriggerName, sendGtmEvent } from '@/constants/gtm'
import { publishProject } from '@/services/project'
import { useGetActives } from './useGetActives'

export const usePublishModal = (
  onPublishUpdate: (publish: PublishData | null) => void
) => {
  const dispatch = useDispatch<ThunkDispatch<AppState, unknown, Action>>()
  const bodyRef = useRef<HTMLDivElement>(null)
  const [hasHeaderShadow, setHasHeaderShadow] = useState(false)
  const [hasFooterShadow, setHasFooterShadow] = useState(true)
  const [isLoading, setIsLoading] = useState(false)

  const id = useSelector(migrationProjectSelector.getProjectId) // TODO: get project id
  const {
    publishData,
    activePreset,
    activeFont,
    activeContent,
    activeBgColor,
  } = useGetActives()

  const activeTab = useSelector(migrationProjectSelector.getActiveOrFirstTabId)
  const data = usePublishData(activeTab)

  const tabName = useSelector(
    migrationProjectSelector.getProjectTabTitle(activeTab || '')
  )
  const projectTheme = useSelector(migrationProjectSelector.getProjectTheme)

  const pagePreview = publishData?.pagePreview ? publishData.pagePreview : false

  const themeByColor = getThemesByColor(activeBgColor)
  const themeClassName = getThemeClassName(themeByColor)
  const bgWhite = defineStrictWhiteColor(activeBgColor)

  const handleSetPreset = (preset: Preset) => {
    dispatch(setPreset(preset))
  }

  const handleSetFont = (font: PublishFonts) => {
    dispatch(setPublishFont(font))
    dispatch(setPublishRemovePresetName())
  }

  const handleSetContent = (content: PublishContent) => {
    dispatch(setPublishContent(content))
    dispatch(setPublishRemovePresetName())
  }

  const handleSetBgColor = (color: string) => {
    dispatch(setPublishBgColor(color))
    dispatch(setPublishRemovePresetName())
  }

  const handleScroll = () => {
    if (!bodyRef.current) {
      return
    }

    const { scrollTop, scrollHeight, clientHeight } = bodyRef.current

    setHasHeaderShadow(scrollTop > 0)

    // The value 5 is needed so that the shadow appearance logic does not work right at the end of the scroll.
    setHasFooterShadow(scrollTop + clientHeight >= scrollHeight - 5)
  }

  const handlePublish = async () => {
    sendGtmEvent(GtmTriggerName.PUBLISH_GENERATE, 'Click Publish')

    if (id) {
      try {
        setIsLoading(true)
        const response = await publishProject({
          projectId: id,
          searchEngine: false,
          tab: {
            id: activeTab,
            name: tabName,
          },
          content: data,
          withBranding: true,
          pagePreview,
          contentType: activeContent,
          fontType: activeFont,
          bgColor: activeBgColor,
          presetName: activePreset?.name,
          theme: projectTheme,
        })

        dispatch(setPublishData(publishResponseAdapter(response)))
        dispatch(closeModal())
        dispatch(artBoardActions.changeMode('publish'))
      } catch (e) {
        // biome-ignore lint/suspicious/noConsole: intentional
        console.log(e)
      } finally {
        setIsLoading(false)
      }
    }
  }

  const handleUpdatePublish = async () => {
    sendGtmEvent(GtmTriggerName.PUBLISH_UPDATE, 'Click Update')
    onPublishUpdate(publishData)
    dispatch(closeModal())
  }

  const handlePublishPagePreview = (value: boolean) => {
    dispatch(setPublishPagePreview(value))
  }

  return {
    bgWhite,
    activeBgColor,
    themeClassName,
    activeContent,
    pagePreview,
    activeFont,
    hasHeaderShadow,
    bodyRef,
    activePreset,
    hasFooterShadow,
    isLoading,
    handleScroll,
    handleSetPreset,
    handleSetFont,
    handleSetContent,
    handleSetBgColor,
    handleUpdatePublish,
    handlePublish,
    handlePublishPagePreview,
  }
}
